type MenuRecordLike = Record<string, unknown>;

/** 后端分页字段：current、size；列表字段：records、total */
export function normalizeMenuListPage(data: unknown): Api.SystemManage.MenuList {
  if (!data) {
    return { records: [], current: 1, size: 10, total: 0 };
  }

  if (Array.isArray(data)) {
    const records = data.map(item => normalizeMenuRecord(item as MenuRecordLike));
    return {
      records,
      current: 1,
      size: records.length || 10,
      total: records.length
    };
  }

  const record = data as Record<string, unknown>;
  const rawRecords = Array.isArray(record.records) ? record.records : [];
  const records = rawRecords.map(item => normalizeMenuRecord(item as MenuRecordLike));
  const current = Number(record.current ?? 1);
  const size = Number(record.size ?? 10);
  const total = Number(record.total ?? 0);

  return {
    records,
    current: Number.isFinite(current) ? current : 1,
    size: Number.isFinite(size) ? size : 10,
    total: Number.isFinite(total) ? total : records.length
  };
}

export function unwrapMenuTreePayload(data: unknown): Api.SystemManage.MenuTree[] {
  if (!data) return [];
  if (Array.isArray(data)) return data as Api.SystemManage.MenuTree[];

  const record = data as Record<string, unknown>;
  for (const key of ['records', 'list', 'data', 'menus', 'children', 'tree', 'menuTree', 'menuList', 'allMenus']) {
    const value = record[key];
    if (Array.isArray(value)) {
      return value as Api.SystemManage.MenuTree[];
    }
  }

  return [];
}

function toMenuTreeNode(raw: MenuRecordLike): Api.SystemManage.MenuTree {
  const normalized = normalizeMenuRecord(raw);
  return {
    id: normalized.id,
    pId: normalized.pId,
    name: normalized.name,
    label: normalized.name
  };
}

function hasNestedChildren(nodes: Api.SystemManage.MenuTree[]) {
  return nodes.some(node => node.children?.length);
}

/** 将扁平菜单列表按 pId 组装为树 */
export function buildMenuTreeFromFlatList(flat: Api.SystemManage.MenuTree[]): Api.SystemManage.MenuTree[] {
  const nodes = flat.map(item => toMenuTreeNode(item as MenuRecordLike));
  const nodeMap = new Map<number, Api.SystemManage.MenuTree>();

  nodes.forEach(node => {
    nodeMap.set(Number(node.id), { ...node, children: [] });
  });

  const roots: Api.SystemManage.MenuTree[] = [];

  nodeMap.forEach(node => {
    const parentId = Number(node.pId ?? 0);
    if (!parentId || !nodeMap.has(parentId)) {
      roots.push(node);
      return;
    }

    const parent = nodeMap.get(parentId)!;
    parent.children = parent.children ?? [];
    parent.children.push(node);
  });

  return roots;
}

export function prepareMenuTreeData(raw: unknown): Api.SystemManage.MenuTree[] {
  const source = unwrapMenuTreePayload(raw);
  if (!source.length) return [];

  const normalized = source
    .map(item => toMenuTreeNode(item as MenuRecordLike))
    .filter(node => Number.isFinite(Number(node.id)));

  if (!normalized.length) return [];

  const tree = hasNestedChildren(source) ? normalizeMenuTree(source) : buildMenuTreeFromFlatList(normalized);

  return normalizeMenuTree(tree);
}

export function readMenuRecordId(raw: MenuRecordLike) {
  const value = raw.id ?? raw._id ?? raw.menuId ?? raw.key;
  return value === null || value === undefined || value === '' ? NaN : Number(value);
}

/** 兼容后端 pid / parentId / p_id 等字段名 */
export function readMenuParentId(raw: MenuRecordLike) {
  const value = raw.pId ?? raw.pid ?? raw.PId ?? raw.p_id ?? raw.parentId ?? raw.parent_id;

  if (value === null || value === undefined || value === '') {
    return 0;
  }

  return Number(value);
}

export function normalizeMenuRecord(raw: MenuRecordLike | Api.SystemManage.MenuRecord): Api.SystemManage.MenuRecord {
  const record = raw as MenuRecordLike;

  return {
    id: readMenuRecordId(record),
    pId: readMenuParentId(record),
    name: String(record.name ?? record.label ?? record.menuName ?? ''),
    perms: String(record.perms ?? record.menuPerms ?? ''),
    path: (record.path as string | null | undefined) ?? null,
    component: (record.component as string | null | undefined) ?? null,
    icon: (record.icon as string | null | undefined) ?? null,
    visible:
      record.visible !== undefined && record.visible !== null ? Number(record.visible) : undefined,
    status:
      record.status !== undefined && record.status !== null
        ? (Number(record.status) as Api.Common.EnableStatus)
        : null,
    description: (record.description as string | null | undefined) ?? null,
    delFlag: (record.delFlag ?? record.del_flag) as boolean | undefined,
    createDate: (record.createDate ?? record.create_date) as string | undefined,
    updateDate: (record.updateDate ?? record.update_date) as string | undefined,
    createBy: String(record.createBy ?? record.create_by ?? ''),
    createTime: String(record.createTime ?? record.create_time ?? record.createDate ?? record.create_date ?? ''),
    updateBy: String(record.updateBy ?? record.update_by ?? ''),
    updateTime: String(record.updateTime ?? record.update_time ?? record.updateDate ?? record.update_date ?? '')
  };
}

export function normalizeMenuTree(nodes: Api.SystemManage.MenuTree[]): Api.SystemManage.MenuTree[] {
  return nodes.map(node => {
    const normalized = normalizeMenuRecord(node as MenuRecordLike);

    return {
      ...node,
      id: normalized.id,
      pId: normalized.pId,
      name: normalized.name,
      label: node.label ?? normalized.name,
      children: node.children?.length ? normalizeMenuTree(node.children) : undefined
    };
  });
}
